import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Input, Button, Tabs, message, Spin } from 'antd';
import { UserOutlined, LockOutlined, CameraOutlined } from '@ant-design/icons';
import api from '../utils/api';

const { TabPane } = Tabs;

const Login = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [stream, setStream] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    // Clear any existing tokens
    localStorage.clear();
    return () => {
      // Cleanup: stop camera stream when component unmounts
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setCameraError(null);
    } catch (error) {
      console.error('Error accessing camera:', error);
      setCameraError('Failed to access camera. Please check camera permissions.');
      message.error('Camera access denied. Please check your camera permissions.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  };

  const captureImage = () => {
    if (!videoRef.current || !canvasRef.current) return null;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas dimensions to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the video frame to the canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Get the image as base64 string
    return canvas.toDataURL('image/jpeg');
  };

  const handleFaceLogin = async () => {
    if (!stream) {
      message.error('Please enable camera access first');
      return;
    }

    setLoading(true);
    try {
      const imageData = captureImage();
      if (!imageData) {
        throw new Error('Failed to capture image');
      }

      const response = await api.post('/api/login', { face_image: imageData });
      
      // Store tokens and user info
      const { access_token, role, username } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('role', role);
      localStorage.setItem('username', username);
      
      // Update axios default headers
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      message.success('Login successful!');
      stopCamera();
      
      // Navigate based on role
      if (role === 'admin') {
        navigate('/', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      message.error(error.response?.data?.error || 'Face not recognized. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = async (values) => {
    setLoading(true);
    try {
      const response = await api.post('/api/admin-login', values);
      
      // Store tokens and user info
      const { access_token, role, username } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('role', role);
      localStorage.setItem('username', username);
      
      // Update axios default headers
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      message.success('Admin login successful!');
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Admin login error:', error);
      if (error.response?.status === 401) {
        message.error('Invalid credentials');
      } else {
        message.error('Login failed. Please try again.');
      }
      // Clear any partial data
      localStorage.clear();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'linear-gradient(135deg, #1a365d 0%, #2d3748 100%)'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 480,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          borderRadius: 8,
          margin: '20px'
        }}
      >
        <h1 style={{
          textAlign: 'center',
          marginBottom: 24,
          color: '#2d3748',
          fontSize: '24px'
        }}>
          Facial Access Management System
        </h1>

        <Tabs defaultActiveKey="face" centered>
          <TabPane
            tab={
              <span>
                <CameraOutlined />
                Face Login
              </span>
            }
            key="face"
          >
            <div style={{ textAlign: 'center' }}>
              {!stream && !cameraError && (
                <Button
                  type="primary"
                  icon={<CameraOutlined />}
                  onClick={startCamera}
                  style={{ marginBottom: 16 }}
                >
                  Enable Camera
                </Button>
              )}

              {cameraError && (
                <div style={{ color: '#f5222d', marginBottom: 16 }}>
                  {cameraError}
                </div>
              )}

              <div style={{ position: 'relative', marginBottom: 16 }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{
                    width: '100%',
                    maxWidth: 400,
                    borderRadius: 8,
                    display: stream ? 'block' : 'none'
                  }}
                />
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                
                {stream && (
                  <Button
                    type="primary"
                    onClick={handleFaceLogin}
                    loading={loading}
                    style={{ marginTop: 16 }}
                  >
                    Login with Face
                  </Button>
                )}
              </div>
            </div>
          </TabPane>

          <TabPane
            tab={
              <span>
                <UserOutlined />
                Admin Login
              </span>
            }
            key="admin"
          >
            <Form
              form={form}
              onFinish={handleAdminLogin}
              layout="vertical"
            >
              <Form.Item
                name="username"
                rules={[{ required: true, message: 'Please input your username!' }]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Username"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: 'Please input your password!' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Password"
                  size="large"
                />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  block
                  size="large"
                >
                  Login as Admin
                </Button>
              </Form.Item>
            </Form>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default Login;
